/* tutor-1 (C): LAS LLAMADAS DEL TUTOR.

   ARCHIVO EN ASCII A PROPOSITO (regla de la casa para los `.js` nuevos).

   Las llamadas normales van por `api` (axios, la de la casa, tal cual). El MENSAJE va por `fetch` + `ReadableStream`
   porque su respuesta es un flujo SSE: URL `${API_BASE}/api/tutor/conversaciones/{id}/mensajes`, POST, cuerpo
   `FormData` (`texto`, `tz`, `lang`, `fotos`). En la web `credentials: 'include'` y la cabecera `X-CSRF-Token`; en la
   app `credentials: 'omit'` y `Authorization: <token_type> <access_token>`. Un `AbortController` por mensaje (el
   boton "Detener"). Un 401 refresca la sesion (`refrescarSesion`) y reintenta UNA vez.

   El lector parte el flujo por renglon en blanco, entiende `event:` y `data:` y entrega `{evento, datos}`; ignora
   los comentarios (`: ping`). Los modulos de la casa se piden AL LLAMAR (la mina de `lib/api` en el chasis). */
import { IS_APP_BUILD } from './buildTarget';
import { zonaDelAparato } from './tutorDeLaSesion';

function casa() { return require('./api'); }
function api() { return casa().default; }

// ---------------------------------------------------------------------------------------------------- las normales

// remate 2 (S3): `actual` es la conversacion abierta; el servidor no lista las de 0 mensajes salvo esa
export async function leerConversaciones(antes, actual) {
  const params = antes ? { antes, n: 50 } : { n: 50 };
  if (actual) params.actual = actual;
  const res = await api().get('/tutor/conversaciones', { params });
  return (res.data && res.data.conversaciones) || [];
}

export async function crearConversacion(cuerpo = {}) {
  const res = await api().post('/tutor/conversaciones', cuerpo);
  return res.data.conversacion;
}

export async function abrirConversacion(id) {
  const res = await api().get(`/tutor/conversaciones/${encodeURIComponent(id)}`);
  return res.data.conversacion;
}

export async function cambiarConversacion(id, cambios) {
  const res = await api().patch(`/tutor/conversaciones/${encodeURIComponent(id)}`, cambios);
  return res.data.conversacion;
}

export async function borrarConversacion(id) {
  await api().delete(`/tutor/conversaciones/${encodeURIComponent(id)}`);
}

/** tutor-2 (A2.i): la subida contesta por SSE, como el mensaje: `estado` (`leyendo`, `pagina`, `total`), `fin`
 *  (`archivo`) y `error` (`codigo`). Mismo `fetch`, misma cabecera, mismo reintento de 401 y un `AbortController` por
 *  subida (`signal`). Devuelve el archivo del `fin`; un `error` (o una respuesta que no es 2xx) levanta
 *  `ErrorDelTutor` con su codigo. `alEstado` recibe cada `estado`. */
export async function subirArchivo(id, archivo, { signal, alEstado, fetchImpl } = {}) {
  const url = `${casa().API_BASE}/api/tutor/conversaciones/${encodeURIComponent(id)}/archivos`;
  let final = null;
  let codigo = null;
  await pedirSSE(url, () => {
    const datos = new FormData();
    datos.append('archivo', archivo, archivo && archivo.name);
    return datos;
  }, {
    signal,
    fetchImpl,
    alEvento: ({ evento, datos }) => {
      if (evento === 'estado' && alEstado) alEstado(datos || {});
      else if (evento === 'fin') final = (datos && datos.archivo) || null;
      else if (evento === 'error') codigo = (datos && datos.codigo) || 'tutor_failed';
    },
  });
  if (codigo) throw new ErrorDelTutor(codigo, 200);
  if (!final) throw new ErrorDelTutor('tutor_failed', 200);
  return final;
}

/** tutor-2 (C.2): lo que el alumno tiene enfrente a media sesion (`contexto_sesion`). */
export async function guardarSesion(id, contexto) {
  const res = await api().post(`/tutor/conversaciones/${encodeURIComponent(id)}/sesion`, { contexto_sesion: contexto });
  return res.data.contexto_sesion;
}

export async function quitarArchivo(id, archivoId) {
  await api().delete(`/tutor/conversaciones/${encodeURIComponent(id)}/archivos/${encodeURIComponent(archivoId)}`);
}

export async function opinar(id, mensajeId, opinion) {
  const res = await api().post(
    `/tutor/conversaciones/${encodeURIComponent(id)}/mensajes/${encodeURIComponent(mensajeId)}/opinion`, { opinion });
  return res.data.opinion;
}

/** Telemetria del cliente: `tutor_opened`, `tutor_upsell_click`, `tutor_sources_opened`, `tutor_practice_click`.
 *  Nunca falla hacia afuera. */
export function avisarEvento(evento, detalles = {}) {
  try {
    return Promise.resolve(api().post('/tutor/eventos', { evento, detalles })).catch(() => null);
  } catch (e) {
    return Promise.resolve(null);
  }
}

/** El codigo de error de una respuesta de la casa (`detail.error`), o null. */
export function codigoDelError(err) {
  const d = err && err.response && err.response.data && err.response.data.detail;
  return (d && typeof d === 'object' && d.error) || null;
}

// ---------------------------------------------------------------------------------------------------------- el SSE

/** Parte el texto acumulado en eventos completos. Devuelve `{eventos, resto}`. */
export function partirSSE(acumulado) {
  const eventos = [];
  const trozos = acumulado.replace(/\r\n/g, '\n').split('\n\n');
  const resto = trozos.pop();
  for (const bloque of trozos) {
    if (!bloque.trim()) continue;
    let evento = 'message';
    const datos = [];
    let soloComentario = true;
    for (const renglon of bloque.split('\n')) {
      if (renglon.startsWith(':')) continue;
      soloComentario = false;
      if (renglon.startsWith('event:')) evento = renglon.slice(6).trim();
      else if (renglon.startsWith('data:')) datos.push(renglon.slice(5).replace(/^ /, ''));
    }
    if (soloComentario) continue;
    let valor = null;
    try {
      valor = datos.length ? JSON.parse(datos.join('\n')) : null;
    } catch (e) {
      valor = datos.join('\n');
    }
    eventos.push({ evento, datos: valor });
  }
  return { eventos, resto };
}

/** Lee un `ReadableStream` de bytes y entrega cada evento a `alEvento`. */
export async function leerFlujo(cuerpo, alEvento) {
  const lector = cuerpo.getReader();
  const decodificador = new TextDecoder('utf-8');
  let acumulado = '';
  for (;;) {
    const { value, done } = await lector.read();
    if (done) break;
    acumulado += decodificador.decode(value, { stream: true });
    const { eventos, resto } = partirSSE(acumulado);
    acumulado = resto;
    for (const e of eventos) alEvento(e);
  }
  acumulado += decodificador.decode();
  const { eventos } = partirSSE(`${acumulado}\n\n`);
  for (const e of eventos) alEvento(e);
}

async function cabeceras() {
  if (IS_APP_BUILD) {
    const { getTokens } = require('./tokenStore');
    const t = await getTokens();
    return t && t.access_token ? { Authorization: `${t.token_type || 'Bearer'} ${t.access_token}` } : {};
  }
  const token = casa().getCsrfToken();
  return token ? { 'X-CSRF-Token': token } : {};
}

/** Un error con el `codigo` de la casa (`tutor_cap_free`, `tutor_busy`...). */
export class ErrorDelTutor extends Error {
  constructor(codigo, status) {
    super(codigo);
    this.codigo = codigo;
    this.status = status;
  }
}

/** tutor-2 (A3.a): el aparato del mensaje, para la telemetria del admin: `app` en la app; en la web, por el ancho de
 *  la ventana, `telefono` (menos de 768), `tablet` (768 a 1023) o `computadora` (desde 1024). */
export function aparatoDelAparato(ancho) {
  if (IS_APP_BUILD) return 'app';
  const w = Number.isFinite(ancho) ? ancho : (typeof window !== 'undefined' ? window.innerWidth : 1024);
  if (w < 768) return 'telefono';
  if (w < 1024) return 'tablet';
  return 'computadora';
}

/** Manda un mensaje y entrega los eventos del flujo. Levanta `ErrorDelTutor` si el servidor contesta un error
 *  antes del flujo (429 del tope, 400 de las fotos...). `signal` corta el flujo ("Detener"). */
export async function mandarMensaje({ convId, texto, fotos = [], lang, signal, alEvento, fetchImpl }) {
  const url = `${casa().API_BASE}/api/tutor/conversaciones/${encodeURIComponent(convId)}/mensajes`;
  await pedirSSE(url, () => {
    const cuerpo = new FormData();
    cuerpo.append('texto', texto);
    cuerpo.append('tz', zonaDelAparato());
    if (lang) cuerpo.append('lang', lang);
    cuerpo.append('aparato', aparatoDelAparato());
    fotos.forEach((f, i) => cuerpo.append('fotos', f, (f && f.name) || `foto${i + 1}.jpg`));
    return cuerpo;
  }, { signal, alEvento, fetchImpl });
}

/** El POST de un flujo SSE de la casa: `FormData` (armado de nuevo para el reintento), la cabecera de la sesion, el
 *  reintento de 401 y el lector. Un error ANTES del flujo levanta `ErrorDelTutor` con el `detail.error`. */
async function pedirSSE(url, armarCuerpo, { signal, alEvento, fetchImpl }) {
  const pedir = fetchImpl || ((...a) => fetch(...a));
  const armar = async () => ({
    method: 'POST',
    body: armarCuerpo(),
    credentials: IS_APP_BUILD ? 'omit' : 'include',
    headers: { Accept: 'text/event-stream', ...(await cabeceras()) },
    signal,
  });
  let res = await pedir(url, await armar());
  if (res.status === 401 && await casa().refrescarSesion()) {
    res = await pedir(url, await armar());
  }
  if (!res.ok) {
    let codigo = 'tutor_failed';
    try {
      const datos = await res.json();
      const d = datos && datos.detail;
      if (d && typeof d === 'object' && d.error) codigo = d.error;
    } catch (e) {
      /* sin cuerpo legible: el generico */
    }
    throw new ErrorDelTutor(codigo, res.status);
  }
  if (!res.body || typeof res.body.getReader !== 'function') {
    const texto2 = await res.text();
    partirSSE(`${texto2}\n\n`).eventos.forEach(alEvento);
    return;
  }
  await leerFlujo(res.body, alEvento);
}

// ------------------------------------------------------------------------------------- las imagenes con sesion

/** Las fotos y paginas se sirven con sesion. En la web la cookie viaja sola y basta la URL; en la app hay que
 *  pedirlas con la cabecera y pintarlas desde un `blob:`. */
export function urlDeFoto(msgId, n) {
  return `${casa().API_BASE}/api/tutor/fotos/${encodeURIComponent(msgId)}/${n}`;
}

export async function cargarImagen(url, fetchImpl) {
  if (!IS_APP_BUILD) return url;
  const pedir = fetchImpl || ((...a) => fetch(...a));
  const res = await pedir(url, { credentials: 'omit', headers: await cabeceras() });
  if (!res.ok) throw new Error(`imagen ${res.status}`);
  return URL.createObjectURL(await res.blob());
}
